import React, { useState, useEffect } from "react";
import { Grid, Paper, Select } from "@material-ui/core";
import {
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import styled from "@emotion/styled";
import clsx from "clsx";
import { useSnackbar } from "notistack";

import { createRecordPatientCare } from "../../../service/endpoint-manager";

import { patientCareValidationSchema } from "../../../utils/PatientCareValidationSchema";
import { getFacilities } from "../../../service/endpoint-manager";
import { PatientPersonalCareQuestions } from "./PatientCareFormQuestions";
import { PatientEnvironmentQuestions } from "./PatientCareFormQuestions";
import { PatientMobilityAidsQuestions } from "./PatientCareFormQuestions";
import { PatientPCCQuestions } from "./PatientCareFormQuestions";

import ButtonTextLoading from "../Shared/ButtonTextLoading";
import styles from "../../../styles.module.css";

const useStyles = makeStyles((theme) => ({
  formPaper: {
    padding: theme.spacing(2),
  },
  toggleContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    margin: theme.spacing(1),
  },
}));

const MuiToggleButton = styled(ToggleButton)(() => ({
  minWidth: "80px !important",
  height: "34px",
  borderRadius: "0px !important",
  color: "#415970",
  "&:first-child": {
    borderTopLeftRadius: "6px !important",
    borderBottomLeftRadius: "6px !important",
  },
  "&:last-child": {
    borderTopRightRadius: "6px !important",
    borderBottomRightRadius: "6px !important",
  },
  "&:hover": {
    backgroundColor: "#FFD7EB",
  },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: "#F516AA",
    color: "white",
  },
}));

const ToggleButtonGroupField = ({
  name,
  value,
  options,
  setFieldValue,
  outcomeExistsPath,
}) => {
  const classes = useStyles();
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(e, value) => {
        if (outcomeExistsPath) {
          setFieldValue(outcomeExistsPath, value === "yes" ? true : false);
        }
        if (value) {
          setFieldValue(name, value);
        } else {
          setFieldValue(name, "");
        }
      }}
      aria-label={name}
      className={clsx(classes.toggleButtonGroup, "togglePickerPPE")}
    >
      {options.map((option) => (
        <MuiToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
        >
          {option.label}
        </MuiToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const PatientCareForm = ({ isDashboard, setIsDashboard }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getFacilities();
        if (response.data.status) {
          setFacilities(response.data.body);
          setSelectedFacility(response.data.body[0].name);
        }
      } catch (error) {
        console.error("Error fetching facilities: ", error);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const selectedFacilityData = facilities.find(
      (facility) => facility.name === selectedFacility
    );
    setUnits(selectedFacilityData ? selectedFacilityData.units : []);
  }, [selectedFacility, facilities]);

  const toggleOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
    { value: "n/a", label: "N/A" },
  ];

  const getInitialValues = () => ({
    facility: "",
    unit: "",
    residentName: "",
    personalCareAudit: {
      hair: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      face: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
        q3: {
          answer: "",
          comment: "",
        },
        q4: {
          answer: "",
          comment: "",
        },
      },
      fingerNails: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      clothing: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
        q3: {
          answer: "",
          comment: "",
        },
      },
      oralCare: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      continenceCare: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      personalItems: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      overallAppearance: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
      },
      compliance: {
        percentage: 0,
      },
    },
    residentEnvironment: {
      bedding: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
        q3: {
          answer: "",
          comment: "",
        },
        q4: {
          answer: "",
          comment: "",
        },
        q5: {
          answer: "",
          comment: "",
        },
      },
      towels: {
        q1: {
          answer: "",
          comment: "",
        },
      },
      compliance: {
        percentage: 0,
      },
    },
    mobilityAids: {
      mobilityAids: {
        q1: {
          answer: "",
          comment: "",
        },
        q2: {
          answer: "",
          comment: "",
        },
        q3: {
          answer: "",
          comment: "",
        },
        q4: {
          answer: "",
          comment: "",
        },
      },
      compliance: {
        percentage: 0,
      },
    },
    pcc: {
      pcc: {
        q1: {
          answer: "",
          comment: "",
        },
      },
      compliance: {
        percentage: 0,
      },
    },
    additionalComments: " - ",
  });

  const handleFormSubmit = (
    values,
    { setSubmitting, resetForm, setFieldValue }
  ) => {
    // calculating complaince percentage values before the form submit
    const calculateCompliancePercentage = (auditSection) => {
      let totalQuestions = 0;
      let yesAnswers = 0;

      // loop over all the questions in the category
      for (const sectionKey in auditSection) {
        if (
          auditSection.hasOwnProperty(sectionKey) &&
          sectionKey !== "compliance"
        ) {
          const questions = auditSection[sectionKey];
          for (const questionKey in questions) {
            if (questions.hasOwnProperty(questionKey)) {
              totalQuestions++;
              if (questions[questionKey].answer === "yes") {
                yesAnswers++;
              }
            }
          }
        }
      }

      return totalQuestions > 0 ? (yesAnswers / totalQuestions).toFixed(3) : 0;
    };

    const personalCareAuditCompliance = calculateCompliancePercentage(
      values.personalCareAudit
    );
    const residentEnvironmentCompliance = calculateCompliancePercentage(
      values.residentEnvironment
    );
    const mobilityAidsCompliance = calculateCompliancePercentage(
      values.mobilityAids
    );
    const pccCompliance = calculateCompliancePercentage(values.pcc);

    setFieldValue(
      "personalCareAudit.compliance.percentage",
      personalCareAuditCompliance
    );
    setFieldValue(
      "residentEnvironment.compliance.percentage",
      residentEnvironmentCompliance
    );
    setFieldValue("mobilityAids.compliance.percentage", mobilityAidsCompliance);

    if (values.additionalComments === "") {
      setFieldValue("additionalComments", " - ");
    }

    const requestData = {
      facility: values.facility,
      unit: values.unit,
      residentName: values.residentName,
      personalCareAudit: {
        ...values.personalCareAudit,
        compliance: {
          percentage: personalCareAuditCompliance,
        },
      },
      residentEnvironment: {
        ...values.residentEnvironment,
        compliance: {
          percentage: residentEnvironmentCompliance,
        },
      },
      mobilityAids: {
        ...values.mobilityAids,
        compliance: {
          percentage: mobilityAidsCompliance,
        },
      },
      pcc: {
        ...values.pcc,
        compliance: {
          percentage: pccCompliance,
        },
      },
      additionalComments: values.additionalComments,
    };

    console.log("Form values: \n", requestData);

    createRecordPatientCare(requestData)
      .then((response) => {
        enqueueSnackbar(`${response?.data?.message}`, {
          variant: "success",
        });
        setSubmitting(false);
        resetForm();
        console.log(response);
        window.location.reload();
      })
      .catch((error) => {
        enqueueSnackbar(`${error}`, {
          variant: "error",
        });
        console.error("Error submitting form:", error);
        setSubmitting(false);
      });
  };

  // retrieve a nested property value from an object based on a given path.
  // If the path doesn't exist, return a default value
  const safeGet = (obj, path, defaultValue) => {
    return path.reduce((acc, part) => acc && acc[part], obj) || defaultValue;
  };

  const errorText1 = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "5px",
            position: "absolute",
            bottom: "-8px",
            left: "5px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const errorText2 = (error) => {
    try {
      return (
        <div
          style={{
            color: "red",
            fontSize: "0.7rem",
            textAlign: "left",
            visibility: error ? "visible" : "hidden",
            marginTop: "5px",
            position: "absolute",
            bottom: "20px",
            left: "35px",
          }}
        >
          {error}
        </div>
      );
    } catch (e) {
      return "";
    }
  };

  const hasErrors = (errors) => Object.keys(errors).length > 0;

  return (
    <div style={{ paddingBottom: "40px" }}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={patientCareValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, errors, isSubmitting }) => {
          return (
            <Form>
              <Paper className={styles.formPaper}>
                <div className={styles.patientCareFormSectionBody}>
                  <Grid
                    container
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    <Grid item className={styles.patientCarePaperGridItem}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="residentName"
                          className={styles.narcoticsFormLabel}
                        >
                          Resident Name
                        </label>
                        <TextField
                          name="residentName"
                          value={values.residentName}
                          onChange={(e) => {
                            setFieldValue("residentName", e.target.value);
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </div>
                      {errorText1(errors?.residentName)}
                    </Grid>
                    <Grid item className={styles.patientCarePaperGridItem}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="facility"
                          className={styles.narcoticsFormLabel}
                        >
                          Facility
                        </label>
                        <Field
                          as={Select}
                          name="facility"
                          variant="outlined"
                          value={values.facility}
                          onChange={(e) => {
                            setFieldValue("facility", e.target.value);
                          }}
                          fullWidth
                        >
                          {facilities.map((facility) => (
                            <MenuItem key={facility.name} value={facility.name}>
                              {facility.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      {errorText1(errors?.facility)}
                    </Grid>
                    <Grid item className={styles.patientCarePaperGridItem}>
                      <div className={styles.singleFormItem}>
                        <label
                          htmlFor="unit"
                          className={styles.narcoticsFormLabel}
                        >
                          Unit
                        </label>
                        <Field
                          as={Select}
                          name="unit"
                          variant="outlined"
                          value={values.unit}
                          onChange={(e) => {
                            setSelectedUnit(e.target.value);
                            setFieldValue("unit", e.target.value);
                          }}
                          fullWidth
                        >
                          {units.map((unit) => (
                            <MenuItem key={unit.id} value={unit.name}>
                              {unit.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </div>
                      {errorText1(errors?.unit)}
                    </Grid>
                  </Grid>
                </div>
              </Paper>

              <Typography
                variant="h3"
                style={{ textAlign: "left", marginTop: "40px" }}
              >
                1. PERSONAL CARE AUDIT
              </Typography>
              {PatientPersonalCareQuestions.map(
                (categoryData, categoryIndex) => (
                  <div
                    key={categoryIndex}
                    style={{ marginTop: categoryIndex === 0 ? "10px" : "40px" }}
                  >
                    <Paper className={styles.formPaper}>
                      <div className={styles.patientCareFormSectionTitle}>
                        <Typography variant="h6" className={styles.momentTitle}>
                          {categoryIndex + 1}. {categoryData.name}
                        </Typography>
                      </div>
                      {categoryData.questions.map((question, questionIndex) => {
                        const categoryKey = categoryData.tag;
                        const questionKey = `q${questionIndex + 1}`;
                        const answer = safeGet(
                          values,
                          [
                            "personalCareAudit",
                            categoryKey,
                            questionKey,
                            "answer",
                          ],
                          ""
                        );
                        const comment = safeGet(
                          values,
                          [
                            "personalCareAudit",
                            categoryKey,
                            questionKey,
                            "comment",
                          ],
                          ""
                        );

                        return (
                          <React.Fragment key={questionIndex}>
                            <Grid container spacing={4} alignItems="center">
                              <Grid
                                item
                                sm={6}
                                style={{ position: "relative" }}
                              >
                                <div
                                  className={styles.observationPointSectionMain}
                                >
                                  <div className={styles.toggleContainer}>
                                    <Typography
                                      variant="body2"
                                      className={styles.observationPointMargin}
                                    >
                                      {question}
                                    </Typography>
                                    <ToggleButtonGroupField
                                      name={`personalCareAudit.${categoryKey}.${questionKey}.answer`}
                                      value={answer}
                                      options={toggleOptions}
                                      setFieldValue={setFieldValue}
                                      className={styles.observationPointMargin}
                                    />
                                  </div>
                                  {errorText2(
                                    errors?.personalCareAudit?.[categoryKey]?.[
                                      questionKey
                                    ]?.answer
                                  )}
                                </div>
                              </Grid>
                              <Grid
                                item
                                sm={6}
                                style={{ alignContent: "center" }}
                              >
                                <div
                                  className={styles.patientCareFormCommentField}
                                >
                                  <TextField
                                    name={`personalCareAudit.${categoryKey}.${questionKey}.comment`}
                                    value={comment}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `personalCareAudit.${categoryKey}.${questionKey}.comment`,
                                        e.target.value
                                      )
                                    }
                                    label="Comments"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    InputProps={{
                                      rows: 2,
                                      maxRows: 3,
                                      multiline: true,
                                      inputComponent: "textarea",
                                    }}
                                    classes={{
                                      root: styles.narcoticsCommentBoxFieldRoot,
                                    }}
                                    className={"narcoticsCommentBoxField"}
                                  />
                                </div>
                              </Grid>
                            </Grid>
                            {questionIndex <
                              categoryData.questions.length - 1 && <Divider />}
                          </React.Fragment>
                        );
                      })}
                    </Paper>
                  </div>
                )
              )}

              <Typography
                variant="h3"
                style={{ textAlign: "left", marginTop: "40px" }}
              >
                2. RESIDENT ENVIRONMENT
              </Typography>
              {PatientEnvironmentQuestions.map(
                (categoryData, categoryIndex) => (
                  <div
                    key={categoryIndex}
                    style={{ marginTop: categoryIndex === 0 ? "10px" : "40px" }}
                  >
                    <Paper className={styles.formPaper}>
                      <div className={styles.patientCareFormSectionTitle}>
                        <Typography variant="h6" className={styles.momentTitle}>
                          {categoryIndex + 1}. {categoryData.name}
                        </Typography>
                      </div>
                      {categoryData.questions.map((question, questionIndex) => {
                        const categoryKey = categoryData.tag;
                        const questionKey = `q${questionIndex + 1}`;
                        const answer = safeGet(
                          values,
                          [
                            "residentEnvironment",
                            categoryKey,
                            questionKey,
                            "answer",
                          ],
                          ""
                        );
                        const comment = safeGet(
                          values,
                          [
                            "residentEnvironment",
                            categoryKey,
                            questionKey,
                            "comment",
                          ],
                          ""
                        );
                        return (
                          <React.Fragment key={questionIndex}>
                            <Grid container spacing={4} alignItems="center">
                              <Grid
                                item
                                sm={6}
                                style={{ position: "relative" }}
                              >
                                <div
                                  className={styles.observationPointSectionMain}
                                >
                                  <div className={styles.toggleContainer}>
                                    <Typography
                                      variant="body2"
                                      className={styles.observationPointMargin}
                                    >
                                      {question}
                                    </Typography>
                                    <ToggleButtonGroupField
                                      name={`residentEnvironment.${categoryKey}.${questionKey}.answer`}
                                      value={answer}
                                      options={toggleOptions}
                                      setFieldValue={setFieldValue}
                                      className={styles.observationPointMargin}
                                    />
                                  </div>
                                  {errorText2(
                                    errors?.residentEnvironment?.[
                                      categoryKey
                                    ]?.[questionKey]?.answer
                                  )}
                                </div>
                              </Grid>
                              <Grid
                                item
                                sm={6}
                                style={{ alignContent: "center" }}
                              >
                                <div
                                  className={styles.patientCareFormCommentField}
                                >
                                  <TextField
                                    name={`residentEnvironment.${categoryKey}.${questionKey}.comment`}
                                    value={comment}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `residentEnvironment.${categoryKey}.${questionKey}.comment`,
                                        e.target.value
                                      )
                                    }
                                    label="Comments"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    InputProps={{
                                      rows: 2,
                                      maxRows: 3,
                                      multiline: true,
                                      inputComponent: "textarea",
                                    }}
                                    classes={{
                                      root: styles.narcoticsCommentBoxFieldRoot,
                                    }}
                                    className={"narcoticsCommentBoxField"}
                                  />
                                </div>
                              </Grid>
                            </Grid>
                            {questionIndex <
                              categoryData.questions.length - 1 && <Divider />}
                          </React.Fragment>
                        );
                      })}
                    </Paper>
                  </div>
                )
              )}

              <Typography
                variant="h3"
                style={{ textAlign: "left", marginTop: "40px" }}
              >
                3. MOBILITY AIDS
              </Typography>
              {PatientMobilityAidsQuestions.map(
                (categoryData, categoryIndex) => (
                  <div
                    key={categoryIndex}
                    style={{ marginTop: categoryIndex === 0 ? "10px" : "40px" }}
                  >
                    <Paper className={styles.formPaper}>
                      <div className={styles.patientCareFormSectionTitle}>
                        <Typography variant="h6" className={styles.momentTitle}>
                          {categoryIndex + 1}. {categoryData.name}
                        </Typography>
                      </div>
                      {categoryData.questions.map((question, questionIndex) => {
                        const categoryKey = categoryData.tag;
                        const questionKey = `q${questionIndex + 1}`;
                        const answer = safeGet(
                          values,
                          ["mobilityAids", categoryKey, questionKey, "answer"],
                          ""
                        );
                        const comment = safeGet(
                          values,
                          ["mobilityAids", categoryKey, questionKey, "comment"],
                          ""
                        );
                        return (
                          <React.Fragment key={questionIndex}>
                            <Grid container spacing={4} alignItems="center">
                              <Grid
                                item
                                sm={6}
                                style={{ position: "relative" }}
                              >
                                <div
                                  className={styles.observationPointSectionMain}
                                >
                                  <div className={styles.toggleContainer}>
                                    <Typography
                                      variant="body2"
                                      className={styles.observationPointMargin}
                                    >
                                      {question}
                                    </Typography>
                                    <ToggleButtonGroupField
                                      name={`mobilityAids.${categoryKey}.${questionKey}.answer`}
                                      value={answer}
                                      options={toggleOptions}
                                      setFieldValue={setFieldValue}
                                      className={styles.observationPointMargin}
                                    />
                                  </div>
                                  {errorText2(
                                    errors?.mobilityAids?.[categoryKey]?.[
                                      questionKey
                                    ]?.answer
                                  )}
                                </div>
                              </Grid>
                              <Grid
                                item
                                sm={6}
                                style={{ alignContent: "center" }}
                              >
                                <div
                                  className={styles.patientCareFormCommentField}
                                >
                                  <TextField
                                    name={`mobilityAids.${categoryKey}.${questionKey}.comment`}
                                    value={comment}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `mobilityAids.${categoryKey}.${questionKey}.comment`,
                                        e.target.value
                                      )
                                    }
                                    label="Comments"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    InputProps={{
                                      rows: 2,
                                      maxRows: 3,
                                      multiline: true,
                                      inputComponent: "textarea",
                                    }}
                                    classes={{
                                      root: styles.narcoticsCommentBoxFieldRoot,
                                    }}
                                    className={"narcoticsCommentBoxField"}
                                  />
                                </div>
                              </Grid>
                            </Grid>
                            {questionIndex <
                              categoryData.questions.length - 1 && <Divider />}
                          </React.Fragment>
                        );
                      })}
                    </Paper>
                  </div>
                )
              )}

              <Typography
                variant="h3"
                style={{ textAlign: "left", marginTop: "40px" }}
              >
                4. POINT CLICK CARE
              </Typography>
              {PatientPCCQuestions.map((categoryData, categoryIndex) => (
                <div
                  key={categoryIndex}
                  style={{ marginTop: categoryIndex === 0 ? "10px" : "40px" }}
                >
                  <Paper className={styles.formPaper}>
                    <div className={styles.patientCareFormSectionTitle}>
                      <Typography variant="h6" className={styles.momentTitle}>
                        {categoryIndex + 1}. {categoryData.name}
                      </Typography>
                    </div>
                    {categoryData.questions.map((question, questionIndex) => {
                      const categoryKey = categoryData.tag;
                      const questionKey = `q${questionIndex + 1}`;
                      const answer = safeGet(
                        values,
                        ["pcc", categoryKey, questionKey, "answer"],
                        ""
                      );
                      const comment = safeGet(
                        values,
                        ["pcc", categoryKey, questionKey, "comment"],
                        ""
                      );
                      return (
                        <React.Fragment key={questionIndex}>
                          <Grid container spacing={4} alignItems="center">
                            <Grid item sm={6} style={{ position: "relative" }}>
                              <div
                                className={styles.observationPointSectionMain}
                              >
                                <div className={styles.toggleContainer}>
                                  <Typography
                                    variant="body2"
                                    className={styles.observationPointMargin}
                                  >
                                    {question}
                                  </Typography>
                                  <ToggleButtonGroupField
                                    name={`pcc.${categoryKey}.${questionKey}.answer`}
                                    value={answer}
                                    options={toggleOptions}
                                    setFieldValue={setFieldValue}
                                    className={styles.observationPointMargin}
                                  />
                                </div>
                                {errorText2(
                                  errors?.pcc?.[categoryKey]?.[questionKey]
                                    ?.answer
                                )}
                              </div>
                            </Grid>
                            <Grid
                              item
                              sm={6}
                              style={{ alignContent: "center" }}
                            >
                              <div
                                className={styles.patientCareFormCommentField}
                              >
                                <TextField
                                  name={`mobilityAids.${categoryKey}.${questionKey}.comment`}
                                  value={comment}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `mobilityAids.${categoryKey}.${questionKey}.comment`,
                                      e.target.value
                                    )
                                  }
                                  label="Comments"
                                  variant="outlined"
                                  fullWidth
                                  multiline
                                  maxRows={3}
                                  InputProps={{
                                    rows: 2,
                                    maxRows: 3,
                                    multiline: true,
                                    inputComponent: "textarea",
                                  }}
                                  classes={{
                                    root: styles.narcoticsCommentBoxFieldRoot,
                                  }}
                                  className={"narcoticsCommentBoxField"}
                                />
                              </div>
                            </Grid>
                          </Grid>
                          {questionIndex <
                            categoryData.questions.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </Paper>
                </div>
              ))}

              <div style={{ marginTop: "40px" }}>
                <Paper className={styles.formPaper}>
                  <div className={styles.patientCareFormAdditionalCommentField}>
                    <TextField
                      label="Additional Comments"
                      variant="outlined"
                      value={values.additionalComments}
                      onChange={(e) => {
                        setFieldValue("additionalComments", e.target.value);
                      }}
                      fullWidth
                      multiline
                      maxRows={6}
                      InputProps={{
                        rows: 5,
                        maxRows: 6,
                        multiline: true,
                        inputComponent: "textarea",
                      }}
                      classes={{
                        root: styles.narcoticsCommentBoxFieldRoot,
                      }}
                      className={"narcoticsCommentBoxField"}
                    />
                  </div>
                </Paper>
              </div>

              <div className={styles.narcoticsFormFinalizeBtns}>
                <Button
                  type="reset"
                  variant="outlined"
                  className={styles.redButton}
                  // onClick={() => {}}
                  style={{ marginLeft: "auto" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={styles.primaryButton}
                  disabled={isSubmitting || hasErrors(errors && errors)}
                >
                  <ButtonTextLoading
                    isSubmitting={isSubmitting}
                    body={"SUBMIT"}
                  />
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default PatientCareForm;
